import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  Box,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { StatusChip } from '@pages/customer/common/statusChip/StatusChip';
import { fonts } from '@theme/fontsCustomer';
import type { ApprovalModel } from '@repositories/approvalRepository/models/ApprovalModel';

interface Props {
  approvals: ApprovalModel[];
  isLoading?: boolean;
}

export const ApprovalTable = observer(({ approvals, isLoading }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const handleRowClick = (approvalId: string) => {
    // 결재 상세 페이지로 이동
    navigate(`/subscriptions/approval-mgmt/detail/${approvalId}`);
  };

  if (isLoading) {
    return <div>{t('Loading')}</div>;
  }

  return (
    <TableContainer>
      <StyledTable>
        <TableHead>
          <TableRow>
            <TableCell width="72px" align="center">
              {t('Approval_Table_Number')}
            </TableCell>
            <TableCell width="153px">{t('Approval_Table_Application_Date')}</TableCell>
            <TableCell width="356px">{t('Approval_Table_Item_Name')}</TableCell>
            <TableCell width="153px">{t('Approval_Table_Final_Approver')}</TableCell>
            <TableCell width="153px">{t('Approval_Table_Applicant')}</TableCell>
            <TableCell width="99px">{t('Approval_Table_Status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {approvals.map((approval, idx) => {
            return (
              <TableRow key={approval.id || idx} onClick={() => handleRowClick(approval.id)}>
                <TableCell align="center">{idx + 1}</TableCell>
                <TableCell>
                  {approval.createdDate ? dayjs(approval.createdDate).format(t('DateFormat_YMD')) : '-'}
                </TableCell>
                <TableCell>
                  <ItemInfo>
                    <span className="item-type">{approval.typeStr}</span>
                    <span className="item-name">{approval.title}</span>
                  </ItemInfo>
                </TableCell>
                <TableCell>{approval.finalApproverName || '-'}</TableCell>
                <TableCell>{approval.drafterName || '-'}</TableCell>
                <TableCell>
                  <StatusChip
                    status={approval.statusLabel}
                    backgroundColor={
                      theme.colors[approval.statusBackgroundColor as keyof typeof theme.colors] as string
                    }
                    color={theme.colors[approval.statusTextColor as keyof typeof theme.colors] as string}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
});

const StyledTable = styled(Table)`
  max-width: 986px;
  width: 100%;
  overflow: auto;

  & .MuiTableHead-root .MuiTableCell-root {
    background-color: ${({ theme }) => theme.colors['bg-gray-light-50']};
    ${fonts.Body2};
    font-weight: 700;
    color: ${({ theme: { colors } }) => colors['text-gray-sub-darker']};
  }

  & .MuiTableBody-root {
    .MuiTableRow-root {
      background-color: ${({ theme }) => theme.colors['bg-white']};
      cursor: pointer;

      &:hover {
        background-color: ${({ theme }) => theme.colors['state-white-primary-hover']};
      }
    }
  }
`;

const TableCell = styled(MuiTableCell)`
  ${fonts.Body2};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
  padding: 10px 16px;
`;

const ItemInfo = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;

  & .item-type {
    ${fonts.Caption2};
    color: ${({ theme: { colors } }) => colors['text-gray-light']};
  }

  & .item-name {
    ${fonts.Body2};
    color: ${({ theme: { colors } }) => colors['text-gray-main']};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
